import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  IconButton,
  Tooltip,
  Avatar
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  PersonOff as PersonOffIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  PhoneForwarded as PhoneForwardedIcon,
  Error as ErrorIcon,
  HelpOutline as HelpOutlineIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface StatusBreakdown {
  authentication_status: string;
  count: string;
  avg_grade: string;
  excellent_count: string;
  good_count: string;
  poor_count: string;
  label: string;
  description: string;
  icon: string;
  color: string;
  isSecurity: boolean;
  percentage: string;
}

interface Summary {
  totalAnalyzed: number;
  successfulAuth: number;
  legitimateDenials: number;
  securityIssues: number;
  successRate: string;
}

const AuthStatusAnalytics: React.FC = () => {
  const navigate = useNavigate();
  const [statusBreakdown, setStatusBreakdown] = useState<StatusBreakdown[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [period, setPeriod] = useState('7d');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`/api/auth-status-analytics?period=${period}`);
      
      if (!response.ok) throw new Error('Failed to fetch auth status analytics');
      
      const data = await response.json();
      setStatusBreakdown(data.statusBreakdown || []);
      setSummary(data.summary || null);
    } catch (err) {
      console.error('Error fetching auth status analytics:', err);
      setError('Failed to load authentication status analytics');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const getStatusIcon = (iconName: string, color: string) => {
    const iconProps = { 
      sx: { 
        color: color === 'success' ? 'success.main' :
               color === 'error' ? 'error.main' :
               color === 'warning' ? 'warning.main' :
               color === 'info' ? 'info.main' :
               'text.secondary'
      } 
    };

    switch (iconName) {
      case 'check_circle': return <CheckCircleIcon {...iconProps} />;
      case 'person_off': return <PersonOffIcon {...iconProps} />;
      case 'warning': return <WarningIcon {...iconProps} />;
      case 'info': return <InfoIcon {...iconProps} />;
      case 'phone_forwarded': return <PhoneForwardedIcon {...iconProps} />;
      case 'error': return <ErrorIcon {...iconProps} />;
      default: return <HelpOutlineIcon {...iconProps} />;
    }
  };

  const handleRowClick = (status: string) => {
    // Navigate to filtered view or expand details
    console.log('View details for status:', status);
  };

  if (loading && !statusBreakdown.length) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#080707' }}>
            🔐 Authentication Status Analytics
          </Typography>
          <Typography variant="body2" sx={{ color: '#706E6B' }}>
            Track authentication success rates and security compliance
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Time Period</InputLabel>
            <Select
              value={period}
              label="Time Period"
              onChange={(e) => setPeriod(e.target.value)}
            >
              <MenuItem value="24h">Last 24 Hours</MenuItem>
              <MenuItem value="7d">Last 7 Days</MenuItem>
              <MenuItem value="30d">Last 30 Days</MenuItem>
              <MenuItem value="90d">Last 90 Days</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Refresh data">
            <IconButton onClick={fetchAnalytics} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white'
            }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <TrendingUpIcon sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {summary.totalAnalyzed}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Total Analyzed
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
              color: 'white'
            }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <CheckCircleIcon sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {summary.successfulAuth}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Verified
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              color: 'white'
            }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <InfoIcon sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {summary.legitimateDenials}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Correctly Denied
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: summary.securityIssues > 0 
                ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
                : 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
              color: 'white'
            }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  {summary.securityIssues > 0 ? <ErrorIcon sx={{ fontSize: 40 }} /> : <CheckCircleIcon sx={{ fontSize: 40 }} />}
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {summary.securityIssues}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Security Issues
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Success Rate Card */}
      {summary && (
        <Card sx={{ mb: 4, bgcolor: parseFloat(summary.successRate) >= 95 ? 'success.light' : 'warning.light' }}>
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Overall Authentication Success Rate
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  (Verified + Correctly Denied + Safe Waivers + Escalations) / Total
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {summary.successRate}%
              </Typography>
            </Stack>
            <LinearProgress 
              variant="determinate" 
              value={parseFloat(summary.successRate)} 
              sx={{ height: 12, borderRadius: 6, mt: 2 }}
              color={parseFloat(summary.successRate) >= 95 ? 'success' : 'warning'}
            />
          </CardContent>
        </Card>
      )}

      {/* Status Breakdown Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
            Breakdown by Authentication Status
          </Typography>

          {statusBreakdown.length === 0 && !loading ? (
            <Box textAlign="center" py={4}>
              <Typography variant="body2" color="text.secondary">
                No authentication data available for this period
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Count</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Percentage</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Avg Grade</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Grade Distribution</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {statusBreakdown.map((status) => (
                    <TableRow 
                      key={status.authentication_status}
                      hover
                      sx={{
                        cursor: 'pointer',
                        bgcolor: status.isSecurity ? 'rgba(244, 67, 54, 0.05)' : 'transparent',
                        '&:hover': { bgcolor: status.isSecurity ? 'rgba(244, 67, 54, 0.1)' : 'action.hover' }
                      }}
                      onClick={() => handleRowClick(status.authentication_status)}
                    >
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar sx={{ 
                            bgcolor: status.isSecurity ? 'error.light' : 
                                     status.color === 'success' ? 'success.light' :
                                     status.color === 'warning' ? 'warning.light' :
                                     'info.light'
                          }}>
                            {getStatusIcon(status.icon, status.color)}
                          </Avatar>
                          <Box>
                            <Typography fontWeight={600}>{status.label}</Typography>
                            {status.isSecurity && (
                              <Chip 
                                label="SECURITY ISSUE" 
                                size="small" 
                                color="error"
                                sx={{ height: 18, fontSize: '0.65rem', mt: 0.5 }}
                              />
                            )}
                          </Box>
                        </Stack>
                      </TableCell>
                      
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {status.description}
                        </Typography>
                      </TableCell>
                      
                      <TableCell align="center">
                        <Chip 
                          label={status.count} 
                          color={status.isSecurity ? 'error' : 'primary'}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      
                      <TableCell align="center">
                        <Typography variant="body1" fontWeight={600}>
                          {status.percentage}%
                        </Typography>
                      </TableCell>
                      
                      <TableCell align="center">
                        <Chip 
                          label={`${(parseFloat(status.avg_grade) / 10).toFixed(1)}/10`}
                          color={parseFloat(status.avg_grade) >= 80 ? 'success' : 
                                parseFloat(status.avg_grade) >= 60 ? 'warning' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title={`Excellent (≥8.0): ${status.excellent_count}`}>
                            <Chip 
                              label={status.excellent_count} 
                              size="small" 
                              color="success"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          </Tooltip>
                          <Tooltip title={`Good (6.0-7.9): ${status.good_count}`}>
                            <Chip 
                              label={status.good_count} 
                              size="small" 
                              color="warning"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          </Tooltip>
                          <Tooltip title={`Poor (<6.0): ${status.poor_count}`}>
                            <Chip 
                              label={status.poor_count} 
                              size="small" 
                              color="error"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AuthStatusAnalytics;

